/**
 * Angular Downgraded Components
 *
 * This file registers Angular components for use in AngularJS templates.
 *
 * Usage in AngularJS template:
 * ```html
 * <ng-alert type="warning">Warning message</ng-alert>
 * <ng-spinner></ng-spinner>
 * ```
 *
 * Call registerDowngradedComponents() after Angular bootstrap.
 */
import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';

// Import Angular components to downgrade
import { AlertComponent } from '../shared/components/alert/alert.component';
import { ExampleComponent } from '../features/example/example.component';
import { ZendeskLoginComponent } from '../features/zendesk-login/zendesk-login.component';
import { ZendeskLogoutComponent } from '../features/zendesk-logout/zendesk-logout.component';
// import { SpinnerComponent } from '../shared/components/spinner/spinner.component';
// import { LoaderComponent } from '../shared/components/loader/loader.component';

// Import Angular services to downgrade
import { ApiService } from '../core/api/api.service';
import { AuthenticationService } from '../core/auth/authentication.service';
import { PermissionsService } from '../core/permissions/permissions.service';

/**
 * Register all downgraded Angular components with AngularJS
 *
 * Call this function after Angular has bootstrapped.
 * Components will be available in AngularJS templates with 'ng-' prefix.
 */
export function registerDowngradedComponents(): void {
  const acmeModule = angular.module('acme');

  // ============================================
  // Downgraded Components
  // ============================================

  // AlertComponent - migrated from acme-alert
  // Usage: <ng-alert type="warning">Message</ng-alert>
  acmeModule.directive('ngAlert', downgradeComponent({ component: AlertComponent }));

  // ExampleComponent - example Angular view for routing demo
  // Usage: <ng-example></ng-example>
  // This is used in UI-Router state definitions for named views
  acmeModule.directive('ngExample', downgradeComponent({ component: ExampleComponent }));

  // ZendeskLoginComponent - Zendesk SSO login page (DEV-11861)
  // Usage: <ng-zendesk-login></ng-zendesk-login>
  // Mounted by the /zendesk/login ui-router state.
  acmeModule.directive(
    'ngZendeskLogin',
    downgradeComponent({ component: ZendeskLoginComponent }),
  );

  // ZendeskLogoutComponent - Zendesk SSO logout page (DEV-11861)
  // Usage: <ng-zendesk-logout></ng-zendesk-logout>
  // Mounted by the /zendesk/logout ui-router state.
  acmeModule.directive(
    'ngZendeskLogout',
    downgradeComponent({ component: ZendeskLogoutComponent }),
  );

  // Uncomment as more components are migrated:
  // acmeModule.directive('ngSpinner', downgradeComponent({ component: SpinnerComponent }));
  // acmeModule.directive('ngLoader', downgradeComponent({ component: LoaderComponent }));

  // ============================================
  // Downgraded Services
  // ============================================

  // AuthenticationService (Angular 18) — registered under both legacy names
  // so the 53+ existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/auth/auth.service.js + authentication.service.ts
  const authFactory = downgradeInjectable(AuthenticationService);
  acmeModule.factory('AuthenticationService', authFactory);
  acmeModule.factory('AuthService', authFactory);

  // PermissionsService (Angular 18) — registered under the legacy name so
  // the 112 existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/permissions/permissions.service.js
  acmeModule.factory('PermissionsService', downgradeInjectable(PermissionsService));

  // ApiService (Angular 18 facade) — registered under the legacy name so the
  // 396 existing AngularJS consumers (`apiService.user.login(...)`) keep
  // working without changes. The facade exposes UserApiService and
  // SystemApiService directly; the remaining 55 sub-services are exposed as
  // lazy bridges to the AngularJS legacy services until each one is migrated.
  // Replaces apps/b2b-ui/common/services/api/api.service.js
  acmeModule.factory('ApiService', downgradeInjectable(ApiService));

  console.log(
    '[ngUpgrade] Downgraded components registered: ngAlert, ngExample, ngZendeskLogin, ngZendeskLogout',
  );
  console.log(
    '[ngUpgrade] Downgraded services registered: AuthenticationService, AuthService, PermissionsService, ApiService',
  );
}

/**
 * Example: How to downgrade an Angular component
 *
 * 1. Create the Angular component:
 * ```typescript
 * @Component({
 *   selector: 'app-my-component',
 *   template: `<div>{{message}}</div>`
 * })
 * export class MyComponent {
 *   @Input() message: string;
 *   @Output() clicked = new EventEmitter<void>();
 * }
 * ```
 *
 * 2. Add to SharedModule declarations and exports
 *
 * 3. Register here:
 * ```typescript
 * acmeModule.directive(
 *   'ngMyComponent',
 *   downgradeComponent({
 *     component: MyComponent,
 *     inputs: ['message'],
 *     outputs: ['clicked']
 *   })
 * );
 * ```
 *
 * 4. Use in AngularJS template:
 * ```html
 * <ng-my-component
 *   message="ctrl.message"
 *   on-clicked="ctrl.handleClick()">
 * </ng-my-component>
 * ```
 */

